import { type FC, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { PageOutcomeRubric } from "components/materials/page/outcomes/PageOutcomeRubric";
import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectIsEditModeEnabled,
  selectNestedPageOutcomes,
  selectOutcomeAssessmentsBySubmissionId,
} from "store/selectors";
import { selectAssessmentsByPageId } from "store/selectors/assessment";
import { useHasItemsSelector, useListSelector, useObjectSelector } from "store/store-hooks";
import { isPageDisabled } from "utils/page-utils";
import { useQueryParam } from "utils/routing";

interface PageOutcomeRubricPanelProps {
  pageId: string;
  className?: string;
}

export const PageOutcomeRubricPanel: FC<PageOutcomeRubricPanelProps> = ({ pageId, className }) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["id", "assessments_published_at", "has_linked_submissions"],
  );
  // Nested page outcomes fully rendered via PageOutcomeRubric.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const pageOutcomes = useListSelector((s) => selectNestedPageOutcomes(s, pageId));
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const outcomeAssessments = useListSelector(
    (s) => selectOutcomeAssessmentsBySubmissionId(s, submissionId),
    ["page_outcome_id", "score"],
  );
  const hasAssessments = useHasItemsSelector((s) => selectAssessmentsByPageId(s, pageId));
  const { canAuthorSection } = useCapabilities();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  useEffect(() => {
    if (!page?.id) {
      storeApi.pages.retrieve(pageId);
    }
    if (page?.id && !pageOutcomes.length) {
      storeApi.page_outcomes.list({ page_id: pageId });
    }
  }, [page?.id, pageId, pageOutcomes.length]);

  if (!page) return null;

  return (
    <div className={twMerge("flex h-full w-full flex-wrap gap-3", className)}>
      {!pageOutcomes.length && (
        <div className="body-s w-full py-2 text-black-tint-40">
          {t("outcomes.no_page_outcomes")}
        </div>
      )}

      {pageOutcomes.map((pageOutcome) => (
        <PageOutcomeRubric
          canModify={canAuthorSection && !hasAssessments && isEditModeEnabled}
          className="flex w-full flex-col"
          disabled={isPageDisabled(page)}
          key={pageOutcome.id}
          outcomeAssessment={
            page.assessments_published_at
              ? (outcomeAssessments.find((oa) => oa.page_outcome_id === pageOutcome.id) ??
                undefined)
              : undefined
          }
          pageOutcome={pageOutcome}
        />
      ))}
      {/*
      If the screen is taller than all the outcome rubrics, the flexbox
      tries to position them in the middle and space them out to occupy
      all the space. This extra <div> makes sure that doesn't happen,
      while still preserving the ability to vertically scroll if the screen
      is shorter than all the outcome rubrics.
      */}
      <div className="grow" />
    </div>
  );
};
